import React, { useEffect, useState } from 'react';

export default function AssignXmitModal({
  user,
  open,
  loading,
  error,
  variant = 'assign',
  onCancel,
  onConfirm,
}) {
  const isChange = variant === 'change';
  const currentXmit = user?.quickResponse?.xmitNumber || null;
  const [mode, setMode] = useState(isChange ? 'manual' : 'provision');
  const [xmitNumber, setXmitNumber] = useState('');

  useEffect(() => {
    if (open) {
      setMode(isChange ? 'manual' : 'provision');
      setXmitNumber('');
    }
  }, [open, user?.id, isChange]);

  if (!open || !user) return null;

  const displayName =
    `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
    user.email ||
    'User';

  const canSubmit =
    !loading &&
    (mode === 'provision' || String(xmitNumber).trim().length > 0);

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[9999] p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6 border border-slate-100 shadow-xl flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-12 bg-sky-50 border border-sky-200 text-sky-600 rounded-full flex items-center justify-center text-xl">
          <i className="fas fa-tower-broadcast" />
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="font-bold text-slate-900 text-lg m-0">
            {isChange ? 'Change XMIT' : 'Assign XMIT'}
          </h2>
          <p className="text-sm text-slate-600 m-0">
            {isChange ? (
              <>
                Replace the Quick Response transmitter for <strong>{displayName}</strong>
                {currentXmit ? (
                  <>
                    {' '}
                    (current: <span className="font-mono">{currentXmit}</span>)
                  </>
                ) : null}
                .
              </>
            ) : (
              <>
                Assign a Quick Response transmitter to <strong>{displayName}</strong>
                {user.email ? ` (${user.email})` : ''}.
              </>
            )}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="assign-xmit-mode"
              value="provision"
              checked={mode === 'provision'}
              onChange={() => setMode('provision')}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-semibold text-slate-800">Auto-assign next QGL number</span>
              <span className="block text-xs text-slate-500">
                {isChange
                  ? 'Takes the old device out of service, then assigns the next sequential QGL number'
                  : 'Uses last assigned XMIT + 1 (e.g. QGL000022 → QGL000023) via SiteSearch'}
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="assign-xmit-mode"
              value="manual"
              checked={mode === 'manual'}
              onChange={() => setMode('manual')}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-semibold text-slate-800">Link existing XMIT number</span>
              <span className="block text-xs text-slate-500">Validates the new transmitter via SiteSearch</span>
            </span>
          </label>
        </div>

        {mode === 'manual' && (
          <div>
            <label htmlFor="assign-xmit-number" className="block text-xs font-semibold text-slate-600 mb-2">
              New XMIT number
            </label>
            <input
              id="assign-xmit-number"
              type="text"
              value={xmitNumber}
              onChange={(e) => setXmitNumber(e.target.value)}
              placeholder="e.g. QGL000022"
              autoComplete="off"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
        )}

        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ mode, xmitNumber: xmitNumber.trim() })}
            disabled={!canSubmit}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? isChange
                ? 'Changing…'
                : 'Assigning…'
              : isChange
                ? 'Change XMIT'
                : 'Assign XMIT'}
          </button>
        </div>
      </div>
    </div>
  );
}
